import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { BulkScheduleDialog } from "@/components/admin/BulkScheduleDialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const STATUS = ["Available", "Filling Fast", "Sold Out", "Cancelled"];
const EMPTY = { trip_id: "", date: "", prices: null, override: false, price: 0, offer_price: null, status: "Available", pickup_point: "", total_seats: 20, visible: true, active: true };

export default function DeparturesManage() {
  const [deps, setDeps] = useState([]);
  const [trips, setTrips] = useState([]);
  const [selected, setSelected] = useState([]);
  const [filterTrip, setFilterTrip] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/departures").then(({ data }) => setDeps(data));
  useEffect(() => { load(); api.get("/admin/trips").then(({ data }) => setTrips(data)); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ ...EMPTY, ...d, override: !!d.prices, prices: d.prices || { ...(d.effective_prices || {}) } }); setOpen(true); };

  const save = async () => {
    if (!form.trip_id || !form.date) { toast.error("Trip and date are required"); return; }
    let prices = null;
    if (form.override) {
      prices = { quad: Number(form.prices?.quad), triple: Number(form.prices?.triple), double: Number(form.prices?.double) };
      if (!(prices.quad > 0 && prices.triple > 0 && prices.double > 0)) { toast.error("Enter all three sharing prices or turn off override"); return; }
    }
    const { override, effective_prices, from_price, seats_booked, seats_left, sold_out, trip_name, trip_slug, ...rest } = form;
    const payload = { ...rest, prices, price: prices ? prices.quad : 0, offer_price: null, total_seats: Number(form.total_seats) || 0 };
    try {
      if (editing) await api.put(`/admin/departures/${editing.id}`, payload);
      else await api.post("/admin/departures", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (d) => { await api.delete(`/admin/departures/${d.id}`); toast.success("Deleted"); load(); };
  const toggle = async (d, key) => { const { effective_prices, from_price, seats_booked, seats_left, sold_out, trip_name, trip_slug, ...rest } = d; await api.put(`/admin/departures/${d.id}`, { ...rest, prices: d.prices || null, offer_price: null, [key]: !d[key] }); load(); };

  const shown = filterTrip === "all" ? deps : deps.filter((d) => d.trip_id === filterTrip);
  const allChecked = shown.length > 0 && shown.every((d) => selected.includes(d.id));
  const toggleAll = () => setSelected(allChecked ? selected.filter((id) => !shown.some((d) => d.id === id)) : [...new Set([...selected, ...shown.map((d) => d.id)])]);
  const toggleOne = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const bulkDelete = async () => {
    const { data } = await api.post("/admin/departures/bulk-delete", { ids: selected });
    toast.success(`${data.deleted} departure(s) deleted${data.blocked ? ` · ${data.blocked} kept (have active bookings)` : ""}`);
    setSelected([]); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-heading text-4xl font-semibold">Departures</h1><p className="text-muted-foreground">{deps.length} departure dates</p></div>
        <div className="flex flex-wrap gap-2">
          <BulkScheduleDialog trips={trips} onDone={load} />
          <Button className="rounded-full" onClick={openNew} data-testid="add-departure"><Plus className="h-4 w-4 mr-1" /> Add Departure</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={filterTrip} onValueChange={(v) => { setFilterTrip(v); setSelected([]); }}>
          <SelectTrigger className="w-full sm:w-64" data-testid="dep-filter-trip"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All trips</SelectItem>{trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
        {selected.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" className="rounded-full text-destructive border-destructive/40" data-testid="bulk-delete"><Trash2 className="h-4 w-4 mr-1" /> Delete {selected.length} selected</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Delete {selected.length} departure(s)?</AlertDialogTitle></AlertDialogHeader>
              <p className="text-sm text-muted-foreground">Departures that have pending or confirmed bookings will be kept automatically.</p>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-destructive" onClick={bulkDelete} data-testid="confirm-bulk-delete">Delete</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="rounded-xl border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left"><tr>
            <th className="p-3 w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} aria-label="Select all" data-testid="dep-select-all" /></th>
            <th className="p-3">Trip</th><th className="p-3">Date</th><th className="p-3">Price</th><th className="p-3">Status</th><th className="p-3">Seats</th>
            <th className="p-3">Pickup</th><th className="p-3">Visible</th><th className="p-3">Active</th><th className="p-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {shown.map((d) => (
              <tr key={d.id} className={`border-t hover:bg-muted/30 ${selected.includes(d.id) ? "bg-primary/5" : ""}`} data-testid="departure-row">
                <td className="p-3"><Checkbox checked={selected.includes(d.id)} onCheckedChange={() => toggleOne(d.id)} aria-label="Select" data-testid="dep-select" /></td>
                <td className="p-3 font-medium">{d.trip_name}</td>
                <td className="p-3 font-mono">{d.date}</td>
                <td className="p-3 font-mono text-xs" data-testid="dep-price-cell">{d.effective_prices ? <>Q {inr(d.effective_prices.quad)} · T {inr(d.effective_prices.triple)} · D {inr(d.effective_prices.double)}{!d.prices && <span className="block text-muted-foreground">trip default</span>}</> : inr(d.price)}</td>
                <td className="p-3">{d.sold_out ? <span className="text-destructive font-medium">Sold Out</span> : d.status}</td>
                <td className="p-3 font-mono text-xs" data-testid="dep-seats-cell">
                  {d.total_seats ? <span className={d.seats_left <= 3 ? "text-amber-600 dark:text-amber-400 font-medium" : ""}>{d.seats_booked}/{d.total_seats} booked · {d.seats_left} left</span> : <span className="text-muted-foreground">Unlimited</span>}
                </td>
                <td className="p-3 text-xs">{d.pickup_point}</td>
                <td className="p-3"><Switch checked={d.visible} onCheckedChange={() => toggle(d, "visible")} /></td>
                <td className="p-3"><Switch checked={d.active} onCheckedChange={() => toggle(d, "active")} /></td>
                <td className="p-3"><div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(d)}><Pencil className="h-4 w-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete this departure?</AlertDialogTitle></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(d)} className="bg-destructive">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                  </AlertDialog>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="departure-form">
          <DialogHeader><DialogTitle className="font-heading text-3xl">{editing ? "Edit" : "Add"} Departure</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Trip *</Label>
              <Select value={form.trip_id} onValueChange={(v) => set("trip_id", v)}>
                <SelectTrigger data-testid="dep-trip"><SelectValue placeholder="Select trip" /></SelectTrigger>
                <SelectContent>{trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="dep-date" /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-xl border p-3 space-y-2" data-testid="dep-prices">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.override} onCheckedChange={(v) => { set("override", v); if (v && !form.prices) set("prices", { ...(trips.find((t) => t.id === form.trip_id)?.prices || {}) }); }} data-testid="dep-override" /> Override trip prices for this date</label>
              {form.override ? (
                <div className="grid grid-cols-3 gap-3">
                  {[["quad", "Quad"], ["triple", "Triple"], ["double", "Double"]].map(([k, l]) => (
                    <div key={k}><Label className="text-xs text-muted-foreground">{l} (₹)</Label><Input type="number" min="1" value={form.prices?.[k] ?? ""} onChange={(e) => set("prices", { ...form.prices, [k]: e.target.value })} data-testid={`dep-price-${k}`} /></div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Uses trip prices{(() => { const p = trips.find((t) => t.id === form.trip_id)?.prices; return p ? `: Q ${inr(p.quad)} · T ${inr(p.triple)} · D ${inr(p.double)}` : ""; })()}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Pickup Point</Label><Input value={form.pickup_point} onChange={(e) => set("pickup_point", e.target.value)} /></div>
              <div><Label>Total Seats (0 = unlimited)</Label><Input type="number" min="0" value={form.total_seats} onChange={(e) => set("total_seats", e.target.value)} data-testid="dep-seats" /></div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2"><Switch checked={form.visible} onCheckedChange={(v) => set("visible", v)} /> Visible</label>
              <label className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} /> Active</label>
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-departure">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
